import React from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Stack,
  useTheme
} from "@mui/material";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import PageTransition from "../components/PageTransition";

function PrivacyPage() {
  const theme = useTheme();

  return (
    <PageTransition>
      <Helmet>
        <title>Privacy Policy – David Kooi</title>
        <link rel="canonical" href="https://davidkooi.com/privacy/" />
        <meta name="description" content="Privacy policy for davidkooi.com - information about data collection, usage, and protection practices." />
      </Helmet>
      
      <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.background.default }}>
        <Navigation />
      
      <Container component="main" id="main" maxWidth="md" sx={{ py: 6 }}>
        <Stack spacing={4}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, textAlign: "center" }}>
            Privacy Policy
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            {/* Hard-coded on purpose: a live date claims an edit that never happened,
                and it desyncs the pre-rendered HTML from the client render. */}
            Last updated: October 4, 2026
          </Typography>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Information Collection
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              This website collects minimal information necessary to provide our services. When you use the ChatDDK 
              feature, we temporarily store your messages to maintain conversation context during your session.
              The text of each question you ask is also saved, without your IP address, so the answers can improve.
              ChatDDK&rsquo;s replies are not kept beyond your session.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We may collect basic analytics data such as page views and general usage patterns to improve the website
              experience. Page-view data is reported in aggregate and is not used to identify individual users.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Use of Data
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Any data collected is used solely to:
            </Typography>
            <Typography component="div" variant="body1" paragraph sx={{ lineHeight: 1.8, pl: 2 }}>
              • Provide and maintain our services<br />
              • Improve user experience<br />
              • Monitor and analyze usage patterns<br />
              • Ensure technical functionality and security
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We do not sell, trade, or share your personal information with third parties for marketing purposes.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              ChatDDK Service
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              The ChatDDK AI assistant processes your messages through Anthropic's API (to generate responses)
              and OpenAI's API (to search David's knowledge base). Your conversations are not used to train AI models.
              Session data is automatically cleared after 24 hours of inactivity. The one exception is the text of
              your questions, which is saved in our analytics tool (PostHog) under a random ID that changes with
              each chat session (and when you clear the chat). We do not attach your IP address, browser user
              agent, or name to it, but the text is saved as you typed it, so anything personal you put in a
              question is saved too. Separately, our hosting provider (Render) keeps its own short-lived request
              logs with IP addresses and times but no question text; those times could in principle be matched to
              a saved question.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Please note that ChatDDK does not have access to confidential information and any responses 
              should be considered for informational purposes only.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Data Security
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We implement appropriate technical and organizational security measures to protect against 
              unauthorized access, alteration, disclosure, or destruction of your information. However, 
              no internet transmission is 100% secure, and we cannot guarantee absolute security.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Cookies and Tracking
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              This website may use cookies to enhance user experience and maintain session state. 
              You can control cookie settings through your browser preferences.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Contact Information
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              If you have questions about this Privacy Policy or our data practices, please contact us at 
              hello@skylarkcreations.com.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Policy Updates
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page 
              with an updated revision date. We encourage you to review this policy periodically.
            </Typography>
          </Box>
        </Stack>
      </Container>

      <Footer />
    </Box>
  </PageTransition>
  );
}

export default PrivacyPage;
